import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Mail, Lock, User, Ticket, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import VibemailLogo from '@/components/VibemailLogo';
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon } from 'lucide-react';

const Auth: React.FC = () => {
  // State for form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [betaName, setBetaName] = useState("");
  const [betaEmail, setBetaEmail] = useState("");
  const [betaReason, setBetaReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "register" | "beta">("login");
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const resetPasswordForm = useForm({
    defaultValues: {
      email: ""
    }
  });
  
  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Felder aus.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        throw error;
      }
      
      if (data.session) {
        toast({
          title: "Erfolgreich",
          description: "Sie wurden erfolgreich eingeloggt.",
        });
        navigate("/");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Login fehlgeschlagen",
        description: error.message || "Bitte überprüfen Sie Ihre E-Mail und Ihr Passwort.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !name || !inviteCode) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle erforderlichen Felder aus.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // First check if the invite code is valid
      const { data: inviteData, error: inviteError } = await supabase
        .from('invite_codes')
        .select('*')
        .eq('code', inviteCode)
        .eq('is_active', true)
        .is('used_by', null)
        .single();
      
      if (inviteError || !inviteData) {
        throw new Error("Ungültiger oder bereits verwendeter Einladungscode.");
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            company_name: company || null,
            invite_code: inviteCode
          }
        }
      });
      
      if (error) {
        throw error;
      }
      
      if (data.user) {
        toast({
          title: "Registrierung erfolgreich",
          description: "Ihr Konto wurde erstellt. Sie werden zur Startseite weitergeleitet.",
        });
        navigate("/");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: "Registrierung fehlgeschlagen",
        description: error.message || "Bei der Registrierung ist ein Fehler aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBetaApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!betaName || !betaEmail) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie Ihren Namen und Ihre E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('beta_applications')
        .insert([
          { 
            name: betaName, 
            email: betaEmail, 
            reason: betaReason || null 
          }
        ]);
      
      if (error) {
        throw error;
      }

      // Send confirmation email
      await supabase.functions.invoke('beta-notification', {
        method: 'POST',
        body: {
          type: 'application_received',
          data: {
            name: betaName,
            email: betaEmail
          }
        }
      });
      
      toast({
        title: "Bewerbung eingereicht",
        description: "Vielen Dank für Ihre Bewerbung. Wir werden uns in Kürze bei Ihnen melden.",
      });
      
      setApplicationSubmitted(true);
      setBetaName("");
      setBetaEmail("");
      setBetaReason("");
    } catch (error: any) {
      console.error("Beta application error:", error);
      toast({
        title: "Bewerbung fehlgeschlagen",
        description: error.message || "Bei der Einreichung Ihrer Bewerbung ist ein Fehler aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    const email = resetPasswordForm.getValues().email;
    
    if (!email) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie Ihre E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }
    
    setIsResettingPassword(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "E-Mail gesendet",
        description: "Eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts wurde an Sie gesendet.",
      });
      
      setResetPasswordDialogOpen(false);
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast({
        title: "Fehler beim Zurücksetzen des Passworts",
        description: error.message || "Beim Senden der E-Mail ist ein Fehler aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsResettingPassword(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 flex items-center justify-center p-4">
      <div className="text-center mb-10 absolute top-10">
        <VibemailLogo size="md" className="mx-auto" />
      </div>
      
      <Card className="w-full max-w-md border-gray-800 bg-gray-900/80 text-gray-100 shadow-xl backdrop-blur-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold text-white">
            {activeTab === "login" && "Willkommen zurück"}
            {activeTab === "register" && "Konto erstellen"}
            {activeTab === "beta" && "Für die Beta bewerben"}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {activeTab === "login" && "Melden Sie sich an, um auf Ihre E-Mail-Vorlagen zuzugreifen."}
            {activeTab === "register" && "Registrieren Sie sich mit einem Einladungscode."}
            {activeTab === "beta" && "Bewerben Sie sich für Zugang zur Beta-Version."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "login" | "register" | "beta")} className="w-full">
            <TabsList className="grid grid-cols-3 mb-6 bg-gray-800">
              <TabsTrigger 
                value="login"
                className="data-[state=active]:bg-jade data-[state=active]:text-white"
              >
                Anmelden
              </TabsTrigger>
              <TabsTrigger 
                value="register"
                className="data-[state=active]:bg-jade data-[state=active]:text-white"
              >
                Registrieren
              </TabsTrigger>
              <TabsTrigger 
                value="beta"
                className="data-[state=active]:bg-jade data-[state=active]:text-white"
              >
                Bewerben
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-300">
                    E-Mail
                  </label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre.email@beispiel.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                    />
                    <Mail className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium text-gray-300">
                    Passwort
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 pr-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                    />
                    <Lock className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-2.5 right-3 h-5 w-5 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button 
                      type="button" 
                      onClick={() => setResetPasswordDialogOpen(true)}
                      className="text-sm text-jade hover:text-jade-light"
                    >
                      Passwort vergessen?
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full bg-jade hover:bg-jade-light text-white"
                >
                  {isLoading ? "Anmeldung..." : "Anmelden"}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register" className="space-y-4">
              <Alert className="bg-gray-800 border-gray-700 text-gray-300 mb-4">
                <InfoIcon className="h-4 w-4 text-jade" />
                <AlertTitle className="text-white">Beta Version</AlertTitle>
                <AlertDescription>
                  Diese Anwendung befindet sich in der Beta-Phase. Für die Registrierung benötigen Sie einen Einladungscode.
                </AlertDescription>
              </Alert>
              
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="invite-code" className="text-sm font-medium text-gray-300">
                    Einladungscode <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="invite-code"
                      type="text"
                      placeholder="XXXX-XXXX"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                      required
                    />
                    <Ticket className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="reg-email" className="text-sm font-medium text-gray-300">
                    E-Mail <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="ihre.email@beispiel.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                      required
                    />
                    <Mail className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="reg-password" className="text-sm font-medium text-gray-300">
                    Passwort <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="reg-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 pr-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                      required
                    />
                    <Lock className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-2.5 right-3 h-5 w-5 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-gray-300">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="name"
                      type="text"
                      placeholder="Max Mustermann"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                      required
                    />
                    <User className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="company" className="text-sm font-medium text-gray-300">
                    Unternehmen
                  </label>
                  <Input
                    id="company"
                    type="text"
                    placeholder="Firma GmbH (optional)"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full bg-jade hover:bg-jade-light text-white"
                >
                  {isLoading ? "Registrierung..." : "Registrieren"}
                </Button>
                
                <div className="text-center text-sm text-gray-400">
                  <p>Kein Einladungscode? <button 
                    type="button" 
                    onClick={() => setActiveTab("beta")} 
                    className="text-jade hover:text-jade-light"
                  >
                    Für die Beta bewerben
                  </button></p>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="beta" className="space-y-4">
              {applicationSubmitted ? (
                <div className="text-center py-6">
                  <div className="mx-auto bg-jade/20 rounded-full p-3 w-16 h-16 flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-jade" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">Bewerbung erhalten!</h3>
                  <p className="text-gray-400 mb-6">
                    Vielen Dank für Ihr Interesse. Wir prüfen Ihre Bewerbung und melden uns in Kürze per E-Mail mit einem Einladungscode.
                  </p>
                  <Button
                    onClick={() => setActiveTab("login")}
                    className="bg-jade hover:bg-jade-light text-white"
                  >
                    Zurück zum Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleBetaApplication} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="beta-name" className="text-sm font-medium text-gray-300">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="beta-name"
                        type="text"
                        placeholder="Max Mustermann"
                        value={betaName}
                        onChange={(e) => setBetaName(e.target.value)}
                        className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                        required
                      />
                      <User className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="beta-email" className="text-sm font-medium text-gray-300">
                      E-Mail <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="beta-email"
                        type="email"
                        placeholder="ihre.email@beispiel.de"
                        value={betaEmail}
                        onChange={(e) => setBetaEmail(e.target.value)}
                        className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                        required
                      />
                      <Mail className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="beta-reason" className="text-sm font-medium text-gray-300">
                      Warum möchten Sie teilnehmen?
                    </label>
                    <Textarea
                      id="beta-reason"
                      placeholder="Erzählen Sie uns, warum Sie an der Beta teilnehmen möchten..."
                      value={betaReason}
                      onChange={(e) => setBetaReason(e.target.value)}
                      className="bg-gray-800 border-gray-700 min-h-[100px] text-white placeholder:text-gray-500 focus-visible:ring-jade"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full bg-jade hover:bg-jade-light text-white"
                  >
                    {isLoading ? "Wird gesendet..." : "Bewerbung absenden"}
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex flex-col text-sm text-gray-400">
          <p>
            {activeTab === "login" && (
              <>Noch kein Konto? <button 
                onClick={() => setActiveTab("register")}
                className="text-jade hover:text-jade-light font-medium"
              >
                Registrieren
              </button></>
            )}
            {activeTab === "register" && (
              <>Bereits registriert? <button 
                onClick={() => setActiveTab("login")}
                className="text-jade hover:text-jade-light font-medium"
              >
                Anmelden
              </button></>
            )}
            {activeTab === "beta" && !applicationSubmitted && (
              <>Bereits einen Code erhalten? <button 
                onClick={() => setActiveTab("register")}
                className="text-jade hover:text-jade-light font-medium"
              >
                Registrieren
              </button></>
            )}
          </p>
        </CardFooter>
      </Card>

      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-gray-800">
          <DialogHeader>
            <DialogTitle>Passwort zurücksetzen</DialogTitle>
            <DialogDescription className="text-gray-400">
              Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...resetPasswordForm}>
            <form onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)} className="space-y-4">
              <FormField
                control={resetPasswordForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">E-Mail</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="ihre.email@beispiel.de"
                          className="bg-gray-800 border-gray-700 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-jade"
                          {...field}
                        />
                        <Mail className="absolute top-2.5 left-3 h-5 w-5 text-gray-500" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setResetPasswordDialogOpen(false)}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  Abbrechen
                </Button>
                <Button 
                  type="submit" 
                  className="bg-jade hover:bg-jade-light text-white"
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? "Wird gesendet..." : "Link senden"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
